import React, { useEffect, useRef, useState } from 'react';
import { Braces, ClipboardCheck, Info, LayoutList } from 'lucide-react';
import Card from '../../../components/Card';
import {
  Badge,
  FieldChecklist,
  FormSection,
  LineListEditor,
  MetaRow,
  META_LABEL,
  RuleRowsEditor,
  SaveResetActions,
} from './editorBits';
import { REQUEST_FIELD_GROUPS, humanizeFieldKey } from './requestFields';

const TIERS = [
  { key: 'low', label: 'Low risk', cls: 'green' },
  { key: 'medium', label: 'Medium risk', cls: 'yellow' },
  { key: 'high', label: 'High risk', cls: 'red' },
];

function TierLists({ value = {}, onChange, placeholder }) {
  return (
    <div className="stack-md">
      {TIERS.map((t) => (
        <div key={t.key}>
          <div className="row" style={{ gap: 8, marginBottom: 6, alignItems: 'center' }}>
            <span className={`chip ${t.cls}`}><span className="dot" />{t.label}</span>
          </div>
          <LineListEditor
            value={value?.[t.key] || []}
            onChange={(next) => onChange({ ...(value || {}), [t.key]: next })}
            multiline
            placeholder={placeholder}
          />
        </div>
      ))}
    </div>
  );
}

// TPRM first-pass criteria editor. Written for a non-technical legal editor:
// every section is a plain-language question, internal ids are hidden and
// generated on save, and the raw JSON is tucked behind an "advanced" toggle.
export default function TprmCriteriaEditor({ ctl }) {
  const [view, setView] = useState('form');
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState(null);
  const d = ctl.draft || {};

  // After a save or reset the baseline identity changes; if we're still in
  // raw-JSON view, re-seed the buffer so it doesn't drift from the draft.
  const baseRef = useRef(ctl.base);
  useEffect(() => {
    if (ctl.base !== baseRef.current) {
      baseRef.current = ctl.base;
      if (view === 'json') {
        setJsonText(JSON.stringify(ctl.draft || {}, null, 2));
        setJsonError(null);
      }
    }
  }, [ctl.base, ctl.draft, view]);

  const enterJson = () => {
    setJsonText(JSON.stringify(d, null, 2));
    setJsonError(null);
    setView('json');
  };

  const onJsonChange = (text) => {
    setJsonText(text);
    try {
      const parsed = JSON.parse(text);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        setJsonError('This needs to be a single set of {…} settings.');
        return;
      }
      setJsonError(null);
      ctl.replaceDraft(parsed);
    } catch (e) {
      setJsonError(`Not valid yet: ${e.message}`);
    }
  };

  const handleReset = () => {
    ctl.reset();
    if (view === 'json') {
      setJsonText(JSON.stringify(ctl.base?.criteria || {}, null, 2));
      setJsonError(null);
    }
  };

  const saveDisabledReason = view === 'json' && jsonError ? jsonError : null;

  return (
    <Card
      title="TPRM first-pass criteria"
      icon={ClipboardCheck}
      action={(
        <SaveResetActions
          dirty={ctl.dirty}
          saving={ctl.saving}
          onSave={ctl.save}
          onReset={handleReset}
          saveDisabledReason={saveDisabledReason}
        />
      )}
    >
      <div className="stack-md">
        <div className="callout blue">
          <div className="callout-icon"><Info className="w-4 h-4" /></div>
          <div className="callout-body" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
            These are the rules the AI uses for its <strong>first pass</strong> on a new vendor request.
            They&apos;re advisory — Legal and Security always make the final call. Write everything in plain
            language; you don&apos;t need to know any code.
          </div>
        </div>

        <MetaRow
          version={ctl.version}
          name={ctl.name}
          onVersion={ctl.setVersion}
          onName={ctl.setName}
          badge={<Badge isDefault={ctl.base?.isDefault} updatedAt={ctl.base?.updatedAt} />}
        />

        {view === 'json' ? (
          <div className="stack-sm" style={{ borderTop: '0.5px solid var(--border-primary)', paddingTop: 16 }}>
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ ...META_LABEL, marginBottom: 0 }}>Raw settings (advanced)</div>
              <button type="button" className="btn ghost sm" onClick={() => setView('form')} disabled={!!jsonError}>
                <LayoutList className="w-3.5 h-3.5" />
                Back to form
              </button>
            </div>
            <textarea
              className="textarea mono"
              value={jsonText}
              onChange={(e) => onJsonChange(e.target.value)}
              spellCheck={false}
              style={{
                width: '100%', minHeight: 460, resize: 'vertical', padding: '12px 14px',
                lineHeight: 1.55, fontSize: 12.5,
                borderColor: jsonError ? 'var(--color-red-500)' : undefined,
              }}
            />
            {jsonError && (
              <div className="callout red">
                <div className="callout-body" style={{ fontSize: 12 }}>{jsonError}</div>
              </div>
            )}
          </div>
        ) : (
          <div className="stack-lg" style={{ borderTop: '0.5px solid var(--border-primary)', paddingTop: 16 }}>
            <FormSection
              title="Required information"
              hint="What a vendor request must answer before the AI will score it. Check everything that should be mandatory."
              count={(d.requiredFields || []).length}
            >
              <FieldChecklist
                groups={REQUEST_FIELD_GROUPS}
                value={d.requiredFields || []}
                onChange={(next) => ctl.setField('requiredFields', next)}
                humanize={humanizeFieldKey}
              />
            </FormSection>

            <FormSection title="How the risk level is decided" hint="What puts a vendor in each tier. The AI suggests a level using these; reviewers can override it.">
              <TierLists
                value={d.riskRules || {}}
                onChange={(next) => ctl.setField('riskRules', next)}
                placeholder="e.g. Has access to customer data"
              />
            </FormSection>

            <FormSection
              title="Automatic escalations"
              hint="Situations that automatically raise a vendor's risk or require special approval — no matter what else the request says."
              count={(d.hardGates || []).length}
            >
              <RuleRowsEditor
                value={d.hardGates || []}
                onChange={(next) => ctl.setField('hardGates', next)}
                placeholder="e.g. Vendor will interact with our customers → treat as high risk"
              />
            </FormSection>

            <FormSection
              title="What the AI flags for Legal"
              hint="Issues worth a closer look from Legal. Set how serious each one is."
              count={(d.legalChecks || []).length}
            >
              <RuleRowsEditor
                value={d.legalChecks || []}
                onChange={(next) => ctl.setField('legalChecks', next)}
                withSeverity
                placeholder="e.g. Confidential info shared before a contract is signed — confirm an NDA is in place"
              />
            </FormSection>

            <FormSection
              title="What the AI flags for Security"
              hint="Issues worth a closer look from Security. Set how serious each one is."
              count={(d.securityChecks || []).length}
            >
              <RuleRowsEditor
                value={d.securityChecks || []}
                onChange={(next) => ctl.setField('securityChecks', next)}
                withSeverity
                placeholder="e.g. Vendor will access company systems — needs an access-controls review"
              />
            </FormSection>

            <FormSection title="Documents to request" hint="Diligence documents to ask the vendor for at each risk level.">
              <TierLists
                value={d.recommendedDocuments || {}}
                onChange={(next) => ctl.setField('recommendedDocuments', next)}
                placeholder="e.g. SOC 2 Type II report"
              />
            </FormSection>

            <FormSection title="Reference" hint="Where these rules come from — shown to reviewers, not used for scoring.">
              <input
                className="input"
                value={d.source || ''}
                onChange={(e) => ctl.setField('source', e.target.value)}
                placeholder="e.g. Lightspark TPRM Program Policy, effective 2024-02-29"
              />
            </FormSection>

            <div style={{ borderTop: '0.5px solid var(--border-primary)', paddingTop: 12 }}>
              <button type="button" className="btn ghost sm" onClick={enterJson} style={{ color: 'var(--text-tertiary)' }}>
                <Braces className="w-3.5 h-3.5" />
                Edit raw settings (advanced)
              </button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
